// Pure compute layer for the rota-compliance table (RotaComplianceTable.js).
//
// Each check is expressed as a list of "units" — one per thing being counted
// (a shift, a set shift, or an employee) — tagged with the role group it belongs
// to and whether it violates. computeCompliance() rolls those up into a per-role
// breakdown plus a total, so the UI stays a thin renderer.
//
// Severity drives colour: 'hard' violations should be 0 (the solver produced
// something it shouldn't / the input was infeasible); 'soft' ones are expected
// quality trade-offs. Units are bucketed by the member's role group (row.role) so
// a role column means the same set of people across every check.

import { buildDayIndex, resolveEffectiveRole } from './ScheduleGrid';
import { matchesIdealWindow, matchesBoundaryWindow } from './shiftIdealMatch';

const IDEAL_SUCCESS = new Set(['match-individual', 'match-role']);
const IDEAL_FAIL = 'mismatch';
const BOUNDARY_SUCCESS = new Set(['boundary-individual', 'boundary-role']);
const BOUNDARY_FAIL = 'boundary-mismatch';

const HOURS_TOLERANCE = 0.25; // ignore sub-15-min float noise on hours comparisons

const parseHHMM = (str) => {
  if (typeof str !== 'string') return null;
  const [h, m] = str.split(':').map(Number);
  if (!Number.isFinite(h)) return null;
  return h + (Number.isFinite(m) ? m / 60 : 0);
};

// Number(null) === 0 and Number('') === 0 in JS, so coerce those to null explicitly —
// otherwise a missing Max_Weekly_Hours (null) reads as a real "max 0h" constraint.
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// A min/max bound that's actually set: null/0/negative all mean "no constraint".
const pos = (v) => {
  const n = num(v);
  return n != null && n > 0 ? n : null;
};

// shift.start/end are already numeric hours (open-ended, e.g. 28 for a 04:00 next-day end).
const overlaps = (shift, blockStart, blockEnd) =>
  shift.start < blockEnd && blockStart < shift.end;

const fmtTime = (shift) => `${shift.startDisplay || ''}–${shift.endDisplay || ''}`;

// A counted unit: { role, bad, offender? }. offender only needed when bad.
const unit = (role, bad, offender) => ({ role, bad, offender: bad ? offender : null });

// Resolve which role an open shift's composite shape_of_day_type belongs to (mirrors
// ScheduleGrid / IdealMatchSummary's filtering of open rows into role groups).
const openShiftRole = (sod) => {
  if (!sod) return null;
  if (sod.includes('+')) {
    const parts = sod.split('+').map((p) => p.trim());
    if (parts.includes('Waiter')) return 'Waiter';
    if (parts.includes('Kitchen')) return 'Kitchen';
    return parts[0];
  }
  return sod;
};

// Internal role label → operational area. Mirrors the Python evaluation port's
// roles.mappings._AS_ROLES areas (autoscheduler-lambda-trigger) — keep the two in step.
const _FOH = 'FRONT_OF_HOUSE';
const _BOH = 'BACK_OF_HOUSE';
const _BAR = 'BAR';
const ROLE_TO_OPERATIONAL_AREA = {
  Manager: _FOH, 'Shift Manager': _FOH, Waiter: _FOH, Host: _FOH, Busser: _FOH,
  Expo: _FOH, Cleaner: _FOH, Security: _FOH, Support: _FOH, Cafe: _FOH,
  Kitchen: _BOH, Backliner: _BOH, Broiler: _BOH, Plancha: _BOH, Fryer: _BOH,
  Pit: _BOH, Hotplate: _BOH, 'Kitchen Porter': _BOH, 'Kitchen Manager': _BOH,
  'Tandoor Chef': _BOH, 'Pizza Chef': _BOH,
  Bar: _BAR, 'Bar Manager': _BAR, 'Bar Back': _BAR,
};

// Operational area of a shift's role (a composite shape collapses via openShiftRole first);
// null for a role with no area (TBC/Stint, unmapped partner roles).
const shiftArea = (role) => {
  if (!role) return null;
  const single = typeof role === 'string' && role.includes('+') ? openShiftRole(role) : role;
  return ROLE_TO_OPERATIONAL_AREA[single] || null;
};

// All member rows (real + open), paired with their flattened shifts and role group.
// Open rows (Stint/TBC) keep the original `row` so resolveEffectiveRole works, and their
// shifts are filtered down to the role group the row represents. Real members appear once.
const collectRows = (tableRows, memberSchedule) => {
  const out = [];
  tableRows.forEach((row) => {
    if (row.type !== 'member') return;
    const member = row.member;
    const name = member?.Name;
    if (!name) return;
    const role = row.role || member.Role_Type || 'Other';
    const rawDays = memberSchedule[name] || {};

    let days = rawDays;
    if (row.isOpen && (row.openFilterRole || row.openFilterRoles)) {
      const allowed = row.openFilterRoles || [row.openFilterRole];
      days = {};
      Object.entries(rawDays).forEach(([day, shifts]) => {
        const filtered = shifts.filter((s) => allowed.includes(openShiftRole(s.shape_of_day_type)));
        if (filtered.length > 0) days[day] = filtered;
      });
    }

    const shifts = Object.values(days).flat();
    const assignedHours = shifts.reduce((sum, s) => sum + (s.length || 0), 0);
    const workdays = Object.keys(days).filter((d) => (days[d] || []).length > 0).length;
    out.push({ member, name, role, row, isOpen: !!row.isOpen, shifts, assignedHours, workdays });
  });
  return out;
};

// Real (non-open) member rows only — the denominator for the compliance checks.
const realMembers = (rows) => {
  const seen = new Set();
  return rows.filter((r) => {
    if (r.isOpen || seen.has(r.name)) return false;
    seen.add(r.name);
    return true;
  });
};

// ---- shift-level checks (one unit per assigned shift) -------------------

const availabilityCheck = (members, dayIndex, blockField) => {
  const units = [];
  members.forEach(({ member, name, role, shifts }) => {
    const blocks = Array.isArray(member[blockField]) ? member[blockField] : [];
    shifts.forEach((s) => {
      const idx = dayIndex[String(s.day).toLowerCase()];
      const hit = blocks.some((b) => {
        if (b.day !== idx) return false;
        const bs = parseHHMM(b.start);
        const be = parseHHMM(b.end);
        return bs != null && be != null && overlaps(s, bs, be);
      });
      units.push(unit(role, hit, { name, detail: `${s.day} ${fmtTime(s)}` }));
    });
  });
  return units;
};

// Roles that can legitimately fill a `targetRole` shift: the role itself plus the
// substitutes the site config allows (e.g. a Manager shift lists Shift Manager as a
// substitute, so a Shift Manager covering Manager is valid, not a violation).
const fillersFor = (config, targetRole) => {
  const roleCfg = config?.ROLES?.[targetRole];
  const subs = roleCfg?.ON_SITE?.SUBSTITUTES;
  const set = new Set([targetRole]);
  if (Array.isArray(subs)) subs.forEach((r) => set.add(r));
  return set;
};

const roleCompatibilityCheck = (members, config) => {
  const units = [];
  members.forEach(({ member, name, role, shifts }) => {
    const memberRoles = [member.Role_Type, ...(Array.isArray(member.Secondary_Role_Type) ? member.Secondary_Role_Type : [])]
      .filter(Boolean);
    if (memberRoles.length === 0) return;
    shifts.forEach((s) => {
      const worked = s.role && s.role !== 'TBC' && s.role !== 'Stint' ? s.role : null;
      if (!worked) return; // can't attribute a concrete role — skip rather than false-flag
      // OK if any of the member's roles can fill the worked role (directly or as a substitute).
      const fillers = fillersFor(config, worked);
      const ok = memberRoles.some((r) => fillers.has(r));
      units.push(unit(role, !ok, { name, detail: `${s.day} ${fmtTime(s)} as ${worked}` }));
    });
  });
  return units;
};

const shiftLengthCheck = (members) => {
  const units = [];
  members.forEach(({ member, name, role, shifts }) => {
    const min = pos(member.Min_Shift_Length);
    const max = pos(member.Max_Shift_Length);
    if (min == null && max == null) return;
    shifts.forEach((s) => {
      const len = s.length || 0;
      const tooShort = min != null && len < min - HOURS_TOLERANCE;
      const tooLong = max != null && len > max + HOURS_TOLERANCE;
      const bound = tooShort ? `min ${min}h` : `max ${max}h`;
      units.push(unit(role, tooShort || tooLong, { name, detail: `${s.day} ${fmtTime(s)} = ${len.toFixed(1)}h (${bound})` }));
    });
  });
  return units;
};

// ---- set-shift check (one unit per set shift) ---------------------------

// Summarise a member's availability/preferences on a given (week-start-relative) day
// index — the context for why a set shift might have been dropped.
const availabilityForDay = (member, dayIdx) => {
  if (member == null || dayIdx == null) return '';
  const tags = [];

  // Day-level availability (Early/Late_Availability + Contractual_Days_Off are week-start
  // relative day indices). A day is unavailable when it's a contractual day off, or both
  // availability arrays exist and neither includes it — mirrors isMemberUnavailableOnDay.
  const early = Array.isArray(member.Early_Availability) ? member.Early_Availability : null;
  const late = Array.isArray(member.Late_Availability) ? member.Late_Availability : null;
  const off = Array.isArray(member.Contractual_Days_Off) ? member.Contractual_Days_Off : null;
  const dayOff = (off && off.includes(dayIdx))
    || (!(early === null && late === null) && !(early ? early.includes(dayIdx) : true) && !(late ? late.includes(dayIdx) : true));
  if (dayOff) tags.push('not available this day');

  const onDay = (arr) => (Array.isArray(arr) ? arr : []).filter((b) => b.day === dayIdx).map((b) => `${b.start}–${b.end}`);
  const unavail = onDay(member.Unavailability_Blocks);
  const soft = onDay(member.Soft_Preference_Blocks);
  if (unavail.length) tags.push(`unavailable ${unavail.join(', ')}`);
  if (soft.length) tags.push(`soft-block ${soft.join(', ')}`);
  if (Array.isArray(member.Early_Preference) && member.Early_Preference.includes(dayIdx)) tags.push('prefers early');
  if (Array.isArray(member.Late_Preference) && member.Late_Preference.includes(dayIdx)) tags.push('prefers late');
  return tags.length ? tags.join('; ') : 'no availability blocks';
};

// Prefer the backend's per-set-shift outcomes (HONOURED vs DROPPED_*). Fall back to
// matching each member's Set_Shifts against the schedule by (day index, start) when no
// outcomes were returned. Either way, append the member's availability for that day so
// the reason a set shift wasn't met is visible.
const setShiftCheck = (members, dayIndex, setShiftOutcomes) => {
  if (Array.isArray(setShiftOutcomes) && setShiftOutcomes.length > 0) {
    const byId = new Map(members.map((m) => [m.member?.Id, m]));
    return setShiftOutcomes.map((o) => {
      const m = byId.get(o.employee_id);
      const role = m?.role || 'Other';
      const bad = o && o.status && o.status !== 'HONOURED';
      const name = o.employee_name || m?.name || o.employee_id || 'Unknown';
      // Resolve the day index from the outcome (numeric, or a day name to map).
      const dayIdx = typeof o.day === 'number' ? o.day
        : (o.day != null ? dayIndex[String(o.day).toLowerCase()] : null);
      const avail = bad ? availabilityForDay(m?.member, dayIdx) : '';
      const detail = `${o.date ? `${o.date} ` : ''}${o.start || ''}–${o.end || ''} · ${String(o.status).replace(/_/g, ' ').toLowerCase()}`;
      return unit(role, bad, { name, detail: avail ? `${detail} · avail: ${avail}` : detail });
    });
  }
  const units = [];
  members.forEach(({ member, name, role, shifts }) => {
    const setShifts = Array.isArray(member.Set_Shifts) ? member.Set_Shifts : [];
    setShifts.forEach((ss) => {
      const ssStart = parseHHMM(ss.start);
      const met = shifts.some((s) => {
        const idx = dayIndex[String(s.day).toLowerCase()];
        return idx === ss.day && Math.abs(s.start - ssStart) <= HOURS_TOLERANCE;
      });
      const avail = !met ? availabilityForDay(member, ss.day) : '';
      const detail = `${ss.date || ''} ${ss.start}–${ss.end} (${ss.role || ''})`;
      units.push(unit(role, !met, { name, detail: avail ? `${detail} · avail: ${avail}` : detail }));
    });
  });
  return units;
};

// ---- employee-level hours / days checks (one unit per employee) ---------

const expectedHours = (m) => num(m.Changed_Expected_Hours) ?? num(m.Weekly_Expected_Hours);

// Break minutes a tier list requires for a shift length; 0 if uncovered. Mirrors
// overrides._eval_break_tiers (lo <= length < hi), used only at clamp midpoints.
const evalBreakTiers = (tiers, lengthHours) => {
  for (const tier of tiers) {
    const [lo, hi] = tier.shift_length_range || [];
    if (lo <= lengthHours && lengthHours < hi) return tier.break_length || 0;
  }
  return 0;
};

// Merge personal and legal tiers taking max per sub-interval; mirrors
// overrides._clamp_break_tiers_to_legal so a personal policy can only ever match or
// exceed the statutory minimum, never undercut it.
const clampBreakTiersToLegal = (personTiers, legalTiers) => {
  if (!legalTiers || !legalTiers.length) return personTiers;
  if (!personTiers || !personTiers.length) return legalTiers;
  const points = [
    ...new Set([...personTiers, ...legalTiers].flatMap((t) => t.shift_length_range || [])),
  ].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const lo = points[i];
    const hi = points[i + 1];
    const clamped = Math.max(evalBreakTiers(personTiers, (lo + hi) / 2), evalBreakTiers(legalTiers, (lo + hi) / 2));
    if (clamped === 0) continue;
    if (result.length && result[result.length - 1].break_length === clamped) {
      result[result.length - 1].shift_length_range[1] = hi;
    } else {
      result.push({ shift_length_range: [lo, hi], break_length: clamped });
    }
  }
  return result;
};

// Break tiers to use for this member, mirroring overrides.resolve_break_tiers: a personal
// Break_Tiers override wins (clamped up to the legal minimum) and the role/site policy tiers
// only apply as the fallback. Legal clamp is a no-op when the config omits LEGAL_BREAK_MINIMUMS.
const resolveBreakTiers = (config, role, member) => {
  const youth = !!member.Youth_Worker;
  const breaksAll = config?.ROLES?.[role]?.BREAKS;
  const personTiers = member.Break_Tiers;
  if (!personTiers || !personTiers.length) {
    return (youth ? breaksAll?.YOUTH_BREAKS : breaksAll?.BREAKS) || [];
  }
  const jur = config?.jurisdiction || 'UK';
  const legalTiers = config?.LEGAL_BREAK_MINIMUMS?.[jur]?.[youth ? 'YOUTH' : 'ADULT'] || [];
  return clampBreakTiersToLegal(personTiers, legalTiers);
};

// Required unpaid-break hours for one shift, mirroring the backend's
// required_break_slots_for_shift: only roles with BREAKS.DONT_COUNT_BREAKS net breaks,
// the break minutes floor to whole 30-min slots, and break placement is irrelevant.
export const requiredBreakHours = (config, role, lengthHours, member) => {
  const breaksAll = config?.ROLES?.[role]?.BREAKS;
  if (!breaksAll || !breaksAll.DONT_COUNT_BREAKS) return 0;
  const tiers = resolveBreakTiers(config, role, member);
  if (!Array.isArray(tiers)) return 0;
  let breakMin = 0;
  tiers.forEach((rule) => {
    const rng = rule.shift_length_range;
    if (Array.isArray(rng) && rng[0] < lengthHours && lengthHours <= rng[1]) {
      breakMin = Math.max(breakMin, rule.break_length || 0);
    }
  });
  return Math.floor(breakMin / 30) * 0.5;
};

// Net scheduled hours = gross shift length minus required unpaid breaks per shift, so the
// contracted comparison matches the LP's net target (placed breaks are sparse — never net those).
const netScheduledHours = (shifts, config, member) =>
  shifts.reduce((sum, s) => sum + (s.length || 0) - requiredBreakHours(config, s.role, s.length || 0, member), 0);

const contractedHoursCheck = (members, config) => {
  const units = [];
  members.forEach(({ member, name, role, shifts }) => {
    const isFixed = member.Contract_Type === 'FIXED';
    // FIXED path wins and ignores the contracted constraint (Edge O); otherwise the
    // per-worker MINIMUM/EXACT mode decides direction.
    const mode = isFixed ? null : member.Contracted_Hours_Constraint;
    if (!isFixed && mode !== 'MINIMUM' && mode !== 'EXACT') return;
    let target = expectedHours(member);
    if (target == null || target <= 0) return;
    // MINIMUM floors at min(contracted, expected): contracted is the stable minimum, but a
    // leave-prorated expected below it wins (mirrors the LP / reasons.py). EXACT/FIXED keep
    // the prorated expected as the target.
    if (mode === 'MINIMUM' && member.Contracted_Hours != null) {
      target = Math.min(Number(member.Contracted_Hours), target);
    }
    const scheduled = netScheduledHours(shifts, config, member);
    const under = scheduled < target - HOURS_TOLERANCE;
    const over = scheduled > target + HOURS_TOLERANCE;
    // EXACT flags both over- and under-allocation; FIXED and MINIMUM flag under only.
    const bad = mode === 'EXACT' ? (under || over) : under;
    const dir = under ? 'under' : over ? 'over' : 'ok';
    const modeLabel = mode === 'EXACT' ? 'exact' : mode === 'MINIMUM' ? 'minimum' : 'fixed';
    units.push(unit(role, bad, {
      name,
      detail: `${scheduled.toFixed(1)}h vs ${target}h ${modeLabel}${bad ? ` (${dir})` : ''}`,
    }));
  });
  return units;
};

const minHoursCheck = (members) => {
  const units = [];
  members.forEach(({ member, name, role, assignedHours }) => {
    const min = pos(member.Min_Weekly_Hours);
    if (min == null) return;
    units.push(unit(role, assignedHours < min - HOURS_TOLERANCE, { name, detail: `${assignedHours.toFixed(1)}h vs min ${min}h` }));
  });
  return units;
};

const maxHoursCheck = (members) => {
  const units = [];
  members.forEach(({ member, name, role, assignedHours }) => {
    const max = pos(member.Max_Weekly_Hours);
    if (max == null) return;
    units.push(unit(role, assignedHours > max + HOURS_TOLERANCE, { name, detail: `${assignedHours.toFixed(1)}h vs max ${max}h` }));
  });
  return units;
};

// "Heavily deprived" = short of target hours by at least `thresholdPct`%. So at 50% a
// 10h target flags below 5h; at 70% it flags below 3h (assigned < (1 − threshold) × target).
// A severity tier of the under-hours story, not an independent constraint.
const heavilyDeprivedCheck = (members, thresholdPct) => {
  const floor = (1 - thresholdPct / 100); // fraction of target below which we flag
  const units = [];
  members.forEach(({ member, name, role, assignedHours }) => {
    const target = expectedHours(member) ?? pos(member.Min_Weekly_Hours);
    if (target == null || target <= 0) return;
    const pct = Math.round((assignedHours / target) * 100);
    units.push(unit(role, assignedHours < floor * target, { name, detail: `${assignedHours.toFixed(1)}h of ${target}h (${pct}%)` }));
  });
  return units;
};

const minMaxDaysCheck = (members) => {
  const units = [];
  members.forEach(({ member, name, role, workdays }) => {
    const min = pos(member.Min_Workdays);
    const max = pos(member.Max_Workdays);
    if (min == null && max == null) return;
    const tooFew = min != null && workdays < min;
    const tooMany = max != null && workdays > max;
    const bound = tooFew ? `min ${min}` : `max ${max}`;
    units.push(unit(role, tooFew || tooMany, { name, detail: `${workdays} days (${bound})` }));
  });
  return units;
};

// Roll a flat unit list up into { byRole: { role: {violating,total,offenders} }, total }.
const rollUp = (units) => {
  const byRole = {};
  const total = { violating: 0, total: 0, offenders: [] };
  units.forEach((u) => {
    if (!byRole[u.role]) byRole[u.role] = { violating: 0, total: 0, offenders: [] };
    const bucket = byRole[u.role];
    bucket.total += 1;
    total.total += 1;
    if (u.bad) {
      bucket.violating += 1;
      total.violating += 1;
      if (u.offender) {
        bucket.offenders.push(u.offender);
        total.offenders.push({ ...u.offender, role: u.role });
      }
    }
  });
  return { byRole, total };
};

// Population Gini coefficient of `counts` — 0 (perfectly even) … 1 (one holder).
// Sorted formula Σ (2i−n−1)·x_(i) / (n·Σx). Zero when there is nothing to share: an empty
// population, a single person, or an all-zero vector (nobody opened).
export const gini = (counts) => {
  const xs = (counts || []).filter((v) => v != null).slice().sort((a, b) => a - b);
  const n = xs.length;
  const total = xs.reduce((s, v) => s + v, 0);
  if (n === 0 || total <= 0) return 0;
  let weighted = 0;
  xs.forEach((x, i) => { weighted += (2 * (i + 1) - n - 1) * x; });
  return weighted / (n * total);
};

// ---- rota-analysis metrics (informational, not pass/fail) ---------------
// These reproduce the old "Rota analysis" panel as table rows. They're tagged
// severity 'info' (neutral, never red/amber). Ideal/start-end show the match rate
// (success / evaluated) and list the mismatches when expanded; open shifts shows the
// open proportion; stretch/deprivation are hours-weighted ratios.

// Generic per-role accumulator that bypasses unit()/rollUp so it can hold either
// counts (match/open) or hours (stretch/deprivation).
const emptyResult = () => ({ byRole: {}, total: { violating: 0, total: 0, offenders: [] } });
const bump = (res, role, addNum, addDen, offender) => {
  if (!res.byRole[role]) res.byRole[role] = { violating: 0, total: 0, offenders: [] };
  res.byRole[role].violating += addNum;
  res.byRole[role].total += addDen;
  res.total.violating += addNum;
  res.total.total += addDen;
  if (offender) {
    res.byRole[role].offenders.push(offender);
    res.total.offenders.push({ ...offender, role });
  }
};

const matchMetric = (rows, kind, config, shapeOfDay, buffer) => {
  const res = emptyResult();
  const SUCCESS = kind === 'ideal' ? IDEAL_SUCCESS : BOUNDARY_SUCCESS;
  const FAIL = kind === 'ideal' ? IDEAL_FAIL : BOUNDARY_FAIL;
  const fn = kind === 'ideal' ? matchesIdealWindow : matchesBoundaryWindow;
  rows.forEach(({ name, role, row, shifts }) => {
    shifts.forEach((s) => {
      const m = fn(s, row.member, config, shapeOfDay, buffer, resolveEffectiveRole(s, row));
      if (SUCCESS.has(m)) bump(res, role, 1, 1, null);              // matched: counts to denom only
      else if (m === FAIL) bump(res, role, 0, 1, { name, detail: `${s.day} ${fmtTime(s)}` }); // miss
    });
  });
  // violating = matches (numerator shown as match %); offenders list the misses.
  return res;
};

const openShiftMetric = (rows) => {
  const res = emptyResult();
  rows.forEach(({ name, role, isOpen, shifts }) => {
    shifts.forEach((s) => {
      bump(res, role, isOpen ? 1 : 0, 1, isOpen ? { name, detail: `${s.day} ${fmtTime(s)}` } : null);
    });
  });
  return res;
};

const hoursMetric = (members, kind, config) => {
  const res = emptyResult();
  members.forEach(({ member, name, role, shifts }) => {
    const expected = expectedHours(member);
    if (expected == null || expected <= 0) return;
    // Net required breaks so scheduled is apples-to-apples with the net `expected` target.
    const scheduled = netScheduledHours(shifts, config, member);
    const diff = scheduled - expected;
    const val = kind === 'stretch' ? Math.max(0, diff) : Math.max(0, -diff);
    const offender = val > 0.05
      ? { name, detail: `${val.toFixed(1)}h ${kind === 'stretch' ? 'over' : 'short'} (${scheduled.toFixed(1)}h of ${expected}h)` }
      : null;
    bump(res, role, val, expected, offender);
  });
  return res;
};


// Open/close ROTATION fairness — a Gini index over the eligible holders of the open/close
// duty, for the two engine rules: site key holders (OPEN_CLOSE_RULE) and section
// openers/closers per area (SECTION_OPEN_CLOSE_RULE, only REQUIRES_* areas). Mirror of the
// Python evaluation port (metrics/rotation.py) — keep the two in step.
//
// A shift is an open/close if it lands within MINUTES_AFTER_OPENING / MINUTES_BEFORE_CLOSING
// (default 30) of the day's earliest start / latest end (the site's, or the area's) — the
// rota-derived equivalent of the engine's open/close window. Eligibility mirrors
// overrides.resolve_is_opening_key_holder / can_open_section (a site key holder implicitly
// opens every section). Counts are per qualifying shift, per employee, over the eligible
// holders who worked in scope; encoded {violating: gini*n, total: n}.
//
// Deferred vs the engine (approximations): youth keys on Youth_Worker only; no set-shift /
// pre-doors exemption; opener/closer priority ignored.
const ROTATION_DEFAULT_MINUTES = 30;

const isYouth = (member) => !!(member && member.Youth_Worker);

const isKeyHolder = (member, config, kind) => {
  if (isYouth(member)) return false;
  const flag = member[kind === 'open' ? 'Is_Opening_Key_Holder' : 'Is_Closing_Key_Holder'];
  if (flag !== null && flag !== undefined) return !!flag;
  const keyHolders = (config && config.OPEN_CLOSE_RULE && config.OPEN_CLOSE_RULE.KEY_HOLDERS) || [];
  return keyHolders.includes(member.Role_Name);
};

const canSection = (member, config, kind, area) => {
  if (isYouth(member)) return false;
  if (isKeyHolder(member, config, kind)) return true;
  const field = kind === 'open' ? 'Section_Opener_Operational_Areas' : 'Section_Closer_Operational_Areas';
  return (Array.isArray(member[field]) ? member[field] : []).includes(area);
};

const windowHours = (rule, kind, override) => {
  const key = kind === 'open' ? 'MINUTES_AFTER_OPENING' : 'MINUTES_BEFORE_CLOSING';
  let minutes = override ? override[key] : undefined;
  if (minutes === null || minutes === undefined) minutes = rule ? rule[key] : undefined;
  if (minutes === null || minutes === undefined) minutes = ROTATION_DEFAULT_MINUTES;
  return minutes / 60;
};

const inWindow = (shift, kind, anchor, thresholdHours) =>
  (kind === 'open' ? shift.start - anchor : anchor - shift.end) < thresholdHours;

const rotationPid = (r) => (r.member && r.member.Id) || r.name;

const anchorByDay = (rows, kind, area) => {
  const anchor = new Map();
  rows.forEach((r) => {
    r.shifts.forEach((s) => {
      if (area != null && shiftArea(s.role) !== area) return;
      const v = kind === 'open' ? s.start : s.end;
      const cur = anchor.get(s.day);
      anchor.set(s.day, cur === undefined ? v : (kind === 'open' ? Math.min(cur, v) : Math.max(cur, v)));
    });
  });
  return anchor;
};

const giniBucket = (res, key, counts, names, kind) => {
  const vector = [...counts.values()];
  const g = gini(vector);
  const n = vector.reduce((s, v) => s + v, 0);
  let offender = null;
  if (g > 0 && n) {
    let topId = null;
    let topN = -1;
    counts.forEach((v, id) => { if (v > topN) { topN = v; topId = id; } });
    offender = { name: names.get(topId) || topId, detail: `gini ${g.toFixed(2)} over ${n} ${kind}s (busiest ${topN})` };
  }
  bump(res, key, g * n, n, offender);
};

const siteRotationMetric = (rows, config, kind) => {
  const res = emptyResult();
  const rule = (config && config.OPEN_CLOSE_RULE) || {};
  if (!rule.ACTIVE) return res;
  const threshold = windowHours(rule, kind);
  const anchor = anchorByDay(rows, kind, null);
  const counts = new Map();
  const names = new Map();
  const worked = new Set();
  rows.forEach((r) => {
    if (!isKeyHolder(r.member || {}, config, kind)) return;
    const id = rotationPid(r);
    r.shifts.forEach((s) => {
      worked.add(id);
      names.set(id, r.name);
      if (inWindow(s, kind, anchor.get(s.day), threshold)) counts.set(id, (counts.get(id) || 0) + 1);
    });
  });
  if (worked.size === 0) return res;
  giniBucket(res, 'SITE', new Map([...worked].map((id) => [id, counts.get(id) || 0])), names, kind);
  return res;
};

const sectionRotationMetric = (rows, config, kind) => {
  const res = emptyResult();
  const rule = (config && config.SECTION_OPEN_CLOSE_RULE) || {};
  if (!rule.ACTIVE) return res;
  const sections = rule.SECTIONS || {};
  const requires = kind === 'open' ? 'REQUIRES_OPENER' : 'REQUIRES_CLOSER';
  Object.keys(sections).sort().forEach((area) => {
    const sec = sections[area] || {};
    if (!sec[requires]) return;
    const threshold = windowHours(rule, kind, sec);
    const anchor = anchorByDay(rows, kind, area);
    const counts = new Map();
    const names = new Map();
    const worked = new Set();
    rows.forEach((r) => {
      if (!canSection(r.member || {}, config, kind, area)) return;
      const id = rotationPid(r);
      r.shifts.forEach((s) => {
        if (shiftArea(s.role) !== area) return;
        worked.add(id);
        names.set(id, r.name);
        if (inWindow(s, kind, anchor.get(s.day), threshold)) counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    if (worked.size === 0) return;
    giniBucket(res, area, new Map([...worked].map((id) => [id, counts.get(id) || 0])), names, kind);
  });
  return res;
};

// kind in {'open','close'}; gate in {'site','section'}.
const openCloseRotationMetric = (rows, config, kind, gate) => {
  const real = rows.filter((r) => !r.isOpen);
  return gate === 'site' ? siteRotationMetric(real, config, kind) : sectionRotationMetric(real, config, kind);
};

// ---- labour-band attribution --------------------------------------------
// The band clamp changes demand before the scheduler sees it: min floors add
// demand, max ceilings remove it. `bandDeltas` carries those intervals (from the
// fixture's labour_bands.json or the audit response telemetry), so band-driven
// hours get their own rows and the Overage / Demand removed rows read against the
// site's ORIGINAL demand rather than the clamped one.

const bandDeltaHours = (bandDeltas, direction) =>
  (bandDeltas || [])
    .filter((d) => d.direction === direction)
    .reduce((sum, d) => {
      const start = parseHHMM(d.start);
      const end = parseHHMM(d.end);
      if (start == null || end == null) return sum;
      return sum + Math.max(0, end - start) * (d.heads || 0);
    }, 0);

const bandDeltaMetric = (bandDeltas, direction, originalDemandHours) => {
  const res = emptyResult();
  res.total.total = originalDemandHours;
  const sign = direction === 'added' ? '+' : '-';
  (bandDeltas || []).filter((d) => d.direction === direction).forEach((d) => {
    const start = parseHHMM(d.start);
    const end = parseHHMM(d.end);
    if (start == null || end == null) return;
    const hours = Math.max(0, end - start) * (d.heads || 0);
    res.total.violating += hours;
    res.total.offenders.push({
      name: d.day,
      detail: `${d.shape} · ${sign}${hours.toFixed(1)}h (${d.start}–${d.end} ×${d.heads})`,
    });
  });
  return res;
};

// Staff-hours scheduled above the required headcount per shape/day, summed across
// the week. Reuses App.js's per-slot diff map (scheduled − required per 30-min slot)
// so this row and the timeline "Over/Under" view never disagree. Denominator is the
// site's original demand hours (band deltas backed out), so the % reads as "overage
// relative to what the site asked for". Shape-level by nature, so it stays in the
// total column; offenders break it down per day/shape.
const overageMetric = (slotDiffMap, demandHours) => {
  const res = emptyResult();
  res.total.total = demandHours;
  const collected = [];
  Object.entries(slotDiffMap || {}).forEach(([key, slots]) => {
    const sep = key.indexOf('|');
    const day = key.slice(0, sep);
    const shape = key.slice(sep + 1);
    let overHours = 0;
    let maxOver = 0;
    Object.values(slots).forEach((d) => { if (d > 0) { overHours += d * 0.5; if (d > maxOver) maxOver = d; } });
    if (overHours > 0.05) collected.push({ day, shape, overHours, maxOver });
  });
  collected.sort((a, b) => b.overHours - a.overHours);
  collected.forEach(({ day, shape, overHours, maxOver }) => {
    res.total.violating += overHours;
    res.total.offenders.push({ name: day, detail: `${shape} · +${overHours.toFixed(1)}h (up to +${maxOver} at a time)` });
  });
  return res;
};

// Mirror of overageMetric for the negative side: staff-hours scheduled BELOW the
// required headcount per shape/day, summed across the week. With SET_SHIFT_DROP_DEMAND
// on, this is the leftover demand the LP chose to drop next to a set shift rather than
// create overage to cover.
const removedMetric = (slotDiffMap, demandHours) => {
  const res = emptyResult();
  res.total.total = demandHours;
  const collected = [];
  Object.entries(slotDiffMap || {}).forEach(([key, slots]) => {
    const sep = key.indexOf('|');
    const day = key.slice(0, sep);
    const shape = key.slice(sep + 1);
    let removedHours = 0;
    let maxRemoved = 0;
    Object.values(slots).forEach((d) => { if (d < 0) { removedHours -= d * 0.5; if (-d > maxRemoved) maxRemoved = -d; } });
    if (removedHours > 0.05) collected.push({ day, shape, removedHours, maxRemoved });
  });
  collected.sort((a, b) => b.removedHours - a.removedHours);
  collected.forEach(({ day, shape, removedHours, maxRemoved }) => {
    res.total.violating += removedHours;
    res.total.offenders.push({ name: day, detail: `${shape} · -${removedHours.toFixed(1)}h (up to -${maxRemoved} at a time)` });
  });
  return res;
};

// Placed-break hours for one shift from the backend `breaks` map (keyed by shift_id);
// each entry is a list of {start, end} HH:MM windows. 0 when none placed.
const placedBreakHours = (shift, breaks) => {
  const entry = breaks?.[shift.shift_id];
  if (!Array.isArray(entry) || !entry.length) return 0;
  return entry.reduce((sum, b) => {
    const start = parseHHMM(b.start);
    const end = parseHHMM(b.end);
    return sum + (start != null && end != null ? Math.max(0, end - start) : 0);
  }, 0);
};

// Roles solved individually (binary LP) rather than as anonymous integer headcount —
// Manager (SCHEDULE_WITH_LP) and cross-shape roles like Kitchen Manager. Break hours
// are a headcount-demand metric, so these roles are excluded entirely (both the break
// hours and the gross-hours denominator).
const isIndividuallySolvedRole = (config, role) => {
  if (config?.ROLES?.[role]?.SCHEDULE_WITH_LP === true) return true;
  const crossShape = config?.CROSS_SHAPE_LP_ROLES;
  return !!crossShape && Object.prototype.hasOwnProperty.call(crossShape, role);
};

// Total break hours across the headcount-demand roles, mirroring ScheduleGrid's netting:
// per shift take the larger of the placed break and the unpaid break the shift length
// still requires (requiredBreakHours honours the member's own Break_Tiers). Denominator
// is gross scheduled hours for those roles, so the cell reads as "break time as a share
// of scheduled time"; offenders list each member's total break. Individually-solved
// roles (Manager etc.) are skipped — they aren't part of headcount demand.
const breakHoursMetric = (members, config, breaks) => {
  const res = emptyResult();
  members.forEach(({ member, name, role, shifts }) => {
    if (isIndividuallySolvedRole(config, role)) return;
    let memberBreak = 0;
    let memberGross = 0;
    shifts.forEach((s) => {
      const placed = placedBreakHours(s, breaks);
      const required = requiredBreakHours(config, s.role, s.length || 0, member);
      memberBreak += Math.max(placed, required);
      memberGross += s.length || 0;
    });
    const offender = memberBreak > 0.05
      ? { name, detail: `${memberBreak.toFixed(1)}h break across ${shifts.length} shift${shifts.length === 1 ? '' : 's'}` }
      : null;
    bump(res, role, memberBreak, memberGross, offender);
  });
  return res;
};

// Green / amber / red colour bands per check, as percentages of the cell's ratio.
//   dir 'low'  → lower % is better (violations, open shifts, stretch, deprivation):
//                green below `amber`, amber in [amber, red), red at/above `red`.
//   dir 'high' → higher % is better (match rates): green at/above `amber`,
//                amber in (red, amber), red at/below `red`.
// `amber: 0` with dir 'low' means any value > 0 is at least amber (0% only is green).
// Tune these to taste — they're the single source of truth for cell colours.
export const COLOUR_BANDS = {
  // Availability & set shifts
  hardAvail:     { dir: 'low', amber: 0, red: 5 },
  softAvail:     { dir: 'low', amber: 10, red: 30 },
  setShift:      { dir: 'low', amber: 0, red: 10 },
  // Hours
  contracted:    { dir: 'low', amber: 0, red: 10 },
  minHours:      { dir: 'low', amber: 0, red: 10 },
  maxHours:      { dir: 'low', amber: 0, red: 5 },
  deprived:      { dir: 'low', amber: 0, red: 15 },
  stretch:       { dir: 'low', amber: 10, red: 25 },
  deprivation:   { dir: 'low', amber: 10, red: 25 },
  // Roles, days & shift length
  roleCompat:    { dir: 'low', amber: 0, red: 5 },
  minMaxDays:    { dir: 'low', amber: 0, red: 10 },
  shiftLength:   { dir: 'low', amber: 0, red: 10 },
  // Rota analysis
  idealMatch:    { dir: 'high', amber: 90, red: 75 },
  boundaryMatch: { dir: 'high', amber: 90, red: 75 },
  openShifts:    { dir: 'low', amber: 5, red: 10 },
  openRotation:  { dir: 'low', amber: 30, red: 50 },
  closeRotation: { dir: 'low', amber: 30, red: 50 },
  sectionOpenRotation:  { dir: 'low', amber: 30, red: 50 },
  sectionCloseRotation: { dir: 'low', amber: 30, red: 50 },
  overage:       { dir: 'low', amber: 0, red: 15 },
  removed:       { dir: 'low', amber: 0, red: 15 },
  bandAdded:     { dir: 'low', amber: 10, red: 25 },
  bandRemoved:   { dir: 'low', amber: 10, red: 25 },
  // Break hours are informational, not a violation — thresholds sit above any realistic
  // break/gross ratio so the cell always reads neutral (green).
  breakHours:    { dir: 'low', amber: 50, red: 75 },
};

// Map a cell to a colour status ('ok' | 'warn' | 'bad' | 'na') using its band.
export const bandStatus = (cell, band) => {
  if (!cell || cell.total === 0 || !band) return 'na';
  const pct = (cell.violating / cell.total) * 100;
  if (band.dir === 'high') {
    if (pct <= band.red) return 'bad';
    if (pct < band.amber) return 'warn';
    return 'ok';
  }
  if (pct >= band.red) return 'bad';
  if (pct > band.amber) return 'warn';
  return 'ok';
};

// Assemble all checks into grouped, severity-tagged rows with per-role breakdowns.
export const computeCompliance = ({
  tableRows = [],
  memberSchedule = {},
  config = null,
  shapeOfDay = [],
  slotDiffMap = {},
  breaks = {},
  bandDeltas = null,
  setShiftOutcomes = null,
  deprivedThresholdPct = 50,
  idealMatchEnabled = false,
  idealMatchBuffer = 1,
  boundaryMatchEnabled = false,
  boundaryMatchBuffer = 1,
}) => {
  const dayIndex = buildDayIndex(config);
  const rows = collectRows(tableRows, memberSchedule);
  const members = realMembers(rows);

  // Original demand = clamped demand with the band's additions/removals backed out, so
  // the overage/removed denominators mean the same thing with or without a band in force.
  const clampedDemandHours = (shapeOfDay || []).reduce((s, r) => s + (Number(r.length) || 0), 0);
  const bandAddedHours = bandDeltaHours(bandDeltas, 'added');
  const bandRemovedHours = bandDeltaHours(bandDeltas, 'removed');
  const originalDemandHours = clampedDemandHours - bandAddedHours + bandRemovedHours;

  // Role columns, in tableRows order (matches the schedule grid). Include open-row
  // roles too, since the rota-analysis metrics span open shifts.
  const roles = [];
  rows.forEach((r) => { if (!roles.includes(r.role)) roles.push(r.role); });

  // Checks that stay visible (as grey "—") even with a 0 denominator, instead of being
  // dropped — useful when a constraint is expected but not configured for this site.
  const ALWAYS_SHOW = new Set(['minMaxDays']);

  const def = (key, label, group, severity, unitLabel, units) => ({
    key, label, group, severity, unit: unitLabel, band: COLOUR_BANDS[key], alwaysShow: ALWAYS_SHOW.has(key), ...rollUp(units),
  });
  // For pre-aggregated rota-analysis metrics (raw {byRole,total}).
  const defR = (key, label, group, severity, format, result) => ({
    key, label, group, severity, format, band: COLOUR_BANDS[key], alwaysShow: ALWAYS_SHOW.has(key), ...result,
  });

  const checks = [
    // Availability & set shifts
    def('hardAvail', 'Hard availability', 'Availability & set shifts', 'hard', 'shifts',
      availabilityCheck(members, dayIndex, 'Unavailability_Blocks')),
    def('softAvail', 'Soft availability', 'Availability & set shifts', 'soft', 'shifts',
      availabilityCheck(members, dayIndex, 'Soft_Preference_Blocks')),
    def('setShift', 'Set shifts not met', 'Availability & set shifts', 'hard', 'set shifts',
      setShiftCheck(members, dayIndex, setShiftOutcomes)),

    // Hours
    def('contracted', 'Contracted hours', 'Hours', 'hard', 'employees',
      contractedHoursCheck(members, config)),
    def('minHours', 'Min hours', 'Hours', 'hard', 'employees',
      minHoursCheck(members)),
    def('maxHours', 'Max hours (everyone)', 'Hours', 'hard', 'employees',
      maxHoursCheck(members)),
    def('deprived', `Heavily deprived (<${deprivedThresholdPct}%)`, 'Hours', 'soft', 'employees',
      heavilyDeprivedCheck(members, deprivedThresholdPct)),

    // Roles, days & shift length
    def('roleCompat', 'Role compatibility', 'Roles, days & shift length', 'hard', 'shifts',
      roleCompatibilityCheck(members, config)),
    def('minMaxDays', 'Min/Max Days', 'Roles, days & shift length', 'hard', 'employees',
      minMaxDaysCheck(members)),
    def('shiftLength', 'Min/Max Shift length', 'Roles, days & shift length', 'hard', 'shifts',
      shiftLengthCheck(members)),

    // Rota analysis (informational metrics, neutral colour)
    ...(idealMatchEnabled
      ? [defR('idealMatch', 'Ideal-window match', 'Rota analysis', 'info', 'count',
          matchMetric(rows, 'ideal', config, shapeOfDay, idealMatchBuffer))]
      : []),
    ...(boundaryMatchEnabled
      ? [defR('boundaryMatch', 'Start/end match', 'Rota analysis', 'info', 'count',
          matchMetric(rows, 'boundary', config, shapeOfDay, boundaryMatchBuffer))]
      : []),
    defR('openShifts', 'Open shifts', 'Rota analysis', 'info', 'count', openShiftMetric(rows)),
    defR('openRotation', 'Open rotation (Gini)', 'Rota analysis', 'info', 'index', openCloseRotationMetric(rows, config, 'open', 'site')),
    defR('closeRotation', 'Close rotation (Gini)', 'Rota analysis', 'info', 'index', openCloseRotationMetric(rows, config, 'close', 'site')),
    defR('sectionOpenRotation', 'Section open rotation (Gini)', 'Rota analysis', 'info', 'index', openCloseRotationMetric(rows, config, 'open', 'section')),
    defR('sectionCloseRotation', 'Section close rotation (Gini)', 'Rota analysis', 'info', 'index', openCloseRotationMetric(rows, config, 'close', 'section')),
    defR('overage', 'Overage (above required)', 'Rota analysis', 'info', 'hours', overageMetric(slotDiffMap, originalDemandHours)),
    defR('removed', 'Demand removed (below required)', 'Rota analysis', 'info', 'hours', removedMetric(slotDiffMap, originalDemandHours)),
    ...(bandDeltas && bandDeltas.length
      ? [
          defR('bandAdded', 'Overage from band mins', 'Rota analysis', 'info', 'hours',
            bandDeltaMetric(bandDeltas, 'added', originalDemandHours)),
          defR('bandRemoved', 'Demand removed by band maxs', 'Rota analysis', 'info', 'hours',
            bandDeltaMetric(bandDeltas, 'removed', originalDemandHours)),
        ]
      : []),
    defR('breakHours', 'Break hours', 'Rota analysis', 'info', 'hours', breakHoursMetric(members, config, breaks)),

    // Hours-weighted magnitude metrics — informational, grouped with the other Hours rows.
    defR('stretch', 'Stretch', 'Hours', 'info', 'hours', hoursMetric(members, 'stretch', config)),
    defR('deprivation', 'Deprivation', 'Hours', 'info', 'hours', hoursMetric(members, 'deprivation', config)),
  ];

  return { checks, roles };
};

export const GROUP_ORDER = ['Rota analysis', 'Availability & set shifts', 'Hours', 'Roles, days & shift length'];
